import React, { useId, useEffect, useCallback, Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty, noop } from 'lodash-es';
import { Row, Col } from 'reactstrap';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  PartnerOfferModel,
  buildLicensePlateStateCodeForVinPath,
  getLicensePlateErrorState,
} from 'client/data/models/partner-offer-model';
import { VehicleVinModel, buildStylesBasicPathFromVin, VehicleVinEntities } from 'client/data/models/vehicle-vin';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { connect } from 'react-redux';

// Utils
import { EventToolbox } from 'client/utils/event-toolbox';
import { fireSelectVinPixel, fireVinDecodeMatchTypePixel } from 'site-modules/shared/utils/appraisal/common-utils';

// Constants
import { TrackingConstant } from 'client/tracking/constant';

// Context
import {
  useAppraisalTabsConfig,
  useAppraisalTabsState,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';
import { AppraisalFindVinDrawerContext } from 'site-modules/shared/components/find-vin-modal/appraisal-find-vin-drawer';

// Hooks
import { useLicensePlateValidation } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-license-plate-validation';
import { useLicensePlateFields } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-license-plate-fields';
import { useLicensePlateLookup } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-license-plate-lookup';
import { useLicensePlateSubmit } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-license-plate-submit';
import { commonEventData } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/constants/license-plate-tracking';
import { LP_ERROR_KEYS } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/constants/license-plate-constants';

// Utils
import { getLicensePlateTabStyleProperties } from 'site-modules/shared/components/appraisal/appraisal-tabs/license-plate-tab/license-plate-tab-utils';
import {
  getResolvedAppraisalTabViewProps,
  appraisalTabViewPropType,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';

// Components
import { AppraisalTabsSubmitButton } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tabs-submit-button';
import { PrivatePartyButton } from 'site-modules/shared/components/appraisal/private-party-button/private-party-button';
import { LicensePlateTabLpInput } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/license-plate-tab/license-plate-tab-lp-input';
import { LicensePlateTabStateCodeInput } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/license-plate-tab/license-plate-tab-state-code-input';
import { MultipleVinsModal } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/multiple-vins-modal/multiple-vins-modal';
import { FieldError } from 'site-modules/shared/components/field-error/field-error';

import 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/license-plate-tab/license-plate-tab-content.scss';

const LP_DISCLAIMER = 'Your license plate will only be used to look up your car. It will not be stored.';

// Renders the VIN decode error and the wide license plate error as a pair. Used in both the desktop and mobile layouts.
function LpFieldErrors({ uniqInputId, shouldShowVinError = false, decodedVinError = '', errorMap, classes = '' }) {
  return (
    <Fragment>
      <FieldError id="vin-errors" error={shouldShowVinError && decodedVinError} classes={classes} />
      <FieldError
        id={`license-plate-landing-input${uniqInputId}-error`}
        error={errorMap[LP_ERROR_KEYS.LICENSE_PLATE_WIDE]}
        classes={classes}
      />
    </Fragment>
  );
}

LpFieldErrors.propTypes = {
  uniqInputId: PropTypes.string.isRequired,
  shouldShowVinError: PropTypes.bool,
  decodedVinError: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  errorMap: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])).isRequired,
  classes: PropTypes.string,
};

export function LicensePlateTabUI({
  vinFromLicensePlate = null,
  licensePlateLookupError = false,
  vinDecodeResponse = null,
  ctaText = '',
  vehicle = null,
  vinEligibility = null,
  vinTabLink = null,
  hideVinLicensePlateBtn = false,
  tabContentModal = null,
  decodedVinError = '',
  onCompleteCallback = noop,
  clearVin = noop,
  clearDecodedVinEligibility = noop,
  isAppraisalValuePage = false,
  isLicensePlateAndVinStep = false,
  isActiveTab = false,
  isLpAndVinFormComponent = false,
  isSecondary = false,
  setStylesAndVin = noop,
  storage = null,
  apiMetrics,
  view = null,
}) {
  const {
    isProjectEveStyle,
    isEmoSearchEnabled,
    isLookUpStyleView,
    isBackgroundStyleTabEnabled,
    isWIDG1125chal1,
    isSellInCityState,
    isVdpEmbedded,
    isMobile,
    isFullWidth,
    isEscapeHatchDrawer,
    isLocationStyleStep,
    isEscapeHatch,
    creativeId,
    onMultipleVinsModalOpen,
    onMultipleVinsModalClose,
    params,
  } = useAppraisalTabsConfig();

  const { licensePlate, setVin, vin } = useAppraisalTabsState();
  const { setIsInnerModalOpened } = useContext(AppraisalFindVinDrawerContext);

  const resolvedView = getResolvedAppraisalTabViewProps({
    view,
    isEmoSearchEnabled,
    isLookUpStyleView,
    isBackgroundStyleTabEnabled,
    isVdpEmbedded,
    isWIDG1125chal1,
    isSellInCityState,
    isFullWidth,
  });

  const { isPrivatePartyBtn, isVinByAddressOverlay, isAddressTab } = resolvedView;

  const uniqInputId = useId();

  const componentStyleProperties = getLicensePlateTabStyleProperties({
    isFullWidth,
    isVinByAddressOverlay,
    isBackgroundStyleTabEnabled,
    isVdpEmbedded,
    isAppraisalValuePage,
    isEscapeHatchDrawer,
    isLocationStyleStep,
    isEscapeHatch,
    isProjectEveStyle,
    isLookUpStyleView: resolvedView.isLookUpStyleView,
    isSecondary,
    isMobile,
    isAddressTab,
  });

  const { FormWrapper, licensePlateColSize, stateCodeColSize, btnWidthMd } = componentStyleProperties;
  // Resolved style flags (isMobile, isEscapeHatch, etc.) are read via `styleProps.*` to avoid
  // aliasing every flag and to keep them distinct from the same-named raw config flags above.
  const styleProps = componentStyleProperties;

  // Validation hook
  const { errorMap, isLpValid, validateFields, clearFieldError, setLpError } = useLicensePlateValidation();

  // License plate fields hook
  const { licensePlateInputValue, stateCodeInputValue, onLicensePlateChange, onStateCodeChange } =
    useLicensePlateFields({
      storage,
      clearFieldError,
    });

  // Lookup hook (provides utilities; no modal state)
  const { lookupVin, decodeAndSetStyles } = useLicensePlateLookup({
    vinFromLicensePlate,
    creativeId,
    apiMetrics,
    licensePlate,
    isLpAndVinFormComponent,
    setStylesAndVin,
  });

  // Submit hook (orchestrates flow + owns modal state)
  const {
    isSubmitting,
    wasFormSubmitted,
    isMultipleVinsModalOpen,
    setIsMultipleVinProcessed,
    closeMultipleVinsModal,
    onSubmit,
  } = useLicensePlateSubmit({
    validateFields,
    setLpError,
    isLpValid,
    vinFromLicensePlate,
    vinDecodeResponse,
    licensePlateLookupError,
    vinTabLink,
    onCompleteCallback,
    decodeAndSetStyles,
    lookupVin,
    isAppraisalValuePage,
    vinEligibility,
    isLicensePlateAndVinStep,
    licensePlateInputValue,
    stateCodeInputValue,
    clearVin,
    clearDecodedVinEligibility,
    isPrivatePartyBtn,
    isWIDG1125chal1,
    isSellInCityState,
  });

  const service = vinFromLicensePlate?.service;
  const vinValueFromLp = vinFromLicensePlate?.vin;
  const isMultiVins = (vinFromLicensePlate?.vins?.length ?? 0) > 1;
  const isVinDecodeResponseEmpty = isEmpty(vinDecodeResponse);
  const decoderMatchType = vinDecodeResponse?.matchType;

  // Fire the license plate lookup service pixel whenever a lookup service resolves.
  useEffect(() => {
    if (service) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          ...commonEventData,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          subaction_name: TrackingConstant.LICENSE_PLATE_LOOKUP_SERVICE,
          creative_id: creativeId,
          value: service,
        },
      });
    }
  }, [creativeId, service]);

  // Sync the decoded VIN from a single-match license plate lookup into shared appraisal tab state.
  useEffect(() => {
    if (!isMultiVins && vinValueFromLp && vin !== vinValueFromLp) {
      setVin(vinValueFromLp);
    }
  }, [isMultiVins, setVin, vin, vinValueFromLp]);

  // Fire the VIN decode match-type pixel once the active tab has a decode response.
  useEffect(() => {
    if (isActiveTab && !isVinDecodeResponseEmpty) {
      fireVinDecodeMatchTypePixel(decoderMatchType, creativeId);
    }
    // Cannot use 'isActiveTab' in deps array, otherwise the pixel fires on each tab activation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeId, decoderMatchType, isVinDecodeResponseEmpty]);

  // Fire the select VIN pixel once a valid license plate lookup resolves a VIN.
  useEffect(() => {
    if (wasFormSubmitted && isLpValid && vinValueFromLp) {
      fireSelectVinPixel(vinValueFromLp, creativeId);
    }
  }, [creativeId, isLpValid, vinValueFromLp, wasFormSubmitted]);

  // Keep the outer find-VIN drawer backdrop static while the nested multi-VIN modal is active.
  useEffect(() => {
    setIsInnerModalOpened(isMultipleVinsModalOpen);
  }, [isMultipleVinsModalOpen, setIsInnerModalOpened]);

  const handleVinFromMultipleVinsSelect = useCallback(
    async selectedVinData => {
      if (!isEmpty(selectedVinData)) {
        const { vin: vinFromMultipleVinsModal } = selectedVinData;
        setVin(vinFromMultipleVinsModal);
        await decodeAndSetStyles(vinFromMultipleVinsModal);
      }
      setIsMultipleVinProcessed(true);
      closeMultipleVinsModal();
    },
    [closeMultipleVinsModal, decodeAndSetStyles, setIsMultipleVinProcessed, setVin]
  );

  const hasError = Object.keys(errorMap).length > 0;
  const hasLicensePlateError = LP_ERROR_KEYS.LICENSE_PLATE in errorMap;
  const hasLicensePlateWideError = LP_ERROR_KEYS.LICENSE_PLATE_WIDE in errorMap;
  const hasStateCodeError = LP_ERROR_KEYS.STATE_CODE in errorMap;
  const shouldShowVinError =
    !isSubmitting && !(hasLicensePlateError || hasStateCodeError || hasLicensePlateWideError) && !!decodedVinError;
  const shouldRenderPrimaryCta = !isAppraisalValuePage && !styleProps.isVinByAddressOverlay;

  const primaryCta = isPrivatePartyBtn ? (
    <PrivatePartyButton
      onClick={onSubmit}
      isSubmitting={isSubmitting}
      isDisabled={isSubmitting || (wasFormSubmitted && !isLpValid)}
      data-test="submit-license-plate-btn"
      isActiveTab={isActiveTab}
      buttonClassName="mt-lg-1_75"
    />
  ) : (
    <AppraisalTabsSubmitButton
      containerClassName={classnames({
        'mt-0_5': resolvedView.isLookUpStyleView,
        'mt-lg-1_75': styleProps.isBackgroundStyleTabEnabled || isProjectEveStyle,
      })}
      buttonClassName="btn-responsive-md-down"
      onSubmit={onSubmit}
      ctaText={ctaText}
      isSubmitting={isSubmitting}
      isDisabled={isSubmitting || (wasFormSubmitted && !isLpValid)}
      data-test="submit-license-plate-btn"
      isActiveTab={isActiveTab}
    />
  );

  return (
    <FormWrapper
      isFullWidth={styleProps.isFullWidth || styleProps.isLocationStyleStep || styleProps.isVinByAddressOverlay}
      isEscapeHatch={styleProps.isEscapeHatch}
    >
      <div
        className={classnames('license-plate-tab', {
          'd-lg-flex align-items-lg-start':
            (styleProps.isBackgroundStyleTabEnabled || isProjectEveStyle) && !styleProps.isAddressTab,
          'w-75':
            (styleProps.isBackgroundStyleTabEnabled || isProjectEveStyle) &&
            !styleProps.isMobile &&
            !styleProps.isAddressTab,
        })}
      >
        <Row
          className={classnames('appraise-by-license-plate-tab', {
            'mt-1': styleProps.isEscapeHatchDrawer,
            'flex-grow-1':
              (styleProps.isBackgroundStyleTabEnabled || isProjectEveStyle) && !resolvedView.isEmoSearchEnabled,
            'me-lg-0_5': (styleProps.isBackgroundStyleTabEnabled || isProjectEveStyle) && !styleProps.isAddressTab,
            'escape-hatch': styleProps.isEscapeHatch,
          })}
          {...(hasLicensePlateWideError
            ? { 'aria-describedby': `license-plate-landing-input${uniqInputId}-error`, 'aria-invalid': true }
            : {})}
        >
          <Col
            {...licensePlateColSize}
            className={classnames('lp-input-container', { 'px-0': styleProps.isLocationStyleStep })}
          >
            <LicensePlateTabLpInput
              licensePlateInputValue={licensePlateInputValue}
              onLicensePlateChange={onLicensePlateChange}
              errorMap={errorMap}
              isVinByAddressOverlay={styleProps.isVinByAddressOverlay}
              isAppraisalValuePage={isAppraisalValuePage}
              isEscapeHatch={styleProps.isEscapeHatch}
              isLocationStyleStep={styleProps.isLocationStyleStep}
              isFontSize12={styleProps.isFontSize12}
              isFontSize16={styleProps.isFontSize16}
              isAddressTab={styleProps.isAddressTab}
            />
          </Col>
          <Col
            {...stateCodeColSize}
            className={classnames('state-code-input-container', { 'px-0': styleProps.isLocationStyleStep })}
          >
            <LicensePlateTabStateCodeInput
              stateCodeInputValue={stateCodeInputValue}
              onStateCodeChange={onStateCodeChange}
              errorMap={errorMap}
              isLocationStyleStep={styleProps.isLocationStyleStep}
              isVinByAddressOverlay={styleProps.isVinByAddressOverlay}
              isAppraisalValuePage={isAppraisalValuePage}
              isEscapeHatch={styleProps.isEscapeHatch}
              isFontSize12={styleProps.isFontSize12}
              isFontSize16={styleProps.isFontSize16}
              isAddressTab={styleProps.isAddressTab}
            />
          </Col>
        </Row>
        <div
          className={classnames({
            'mb-2': styleProps.isVinByAddressOverlay && !styleProps.isAddressTab,
            ...(styleProps.isVinByAddressOverlay &&
              !decodedVinError && {
                'pt-1': !isLicensePlateAndVinStep,
                'pt-0_5': isLicensePlateAndVinStep,
              }),
          })}
        >
          {shouldRenderPrimaryCta ? (
            primaryCta
          ) : (
            <Fragment>
              {!styleProps.isMobile && (
                <Row>
                  <Col xs={12} className={classnames({ 'px-0': styleProps.isLocationStyleStep })}>
                    <LpFieldErrors
                      uniqInputId={uniqInputId}
                      shouldShowVinError={shouldShowVinError}
                      decodedVinError={decodedVinError}
                      errorMap={errorMap}
                      classes="mb-1"
                    />
                  </Col>
                  {isLicensePlateAndVinStep && (
                    <Col xs={12} className="size-14 text-cool-gray-50 mb-1">
                      {LP_DISCLAIMER}
                    </Col>
                  )}
                </Row>
              )}
              {(!hideVinLicensePlateBtn || styleProps.isVinByAddressOverlay) && (
                <Row
                  className={classnames('align-items-center', {
                    'mt-0_5':
                      styleProps.isEscapeHatch || styleProps.isLocationStyleStep || styleProps.isEscapeHatchDrawer,
                  })}
                >
                  {isLicensePlateAndVinStep && styleProps.isMobile && (
                    <Col xs={12} className="size-14 text-cool-gray-50 mb-1">
                      {LP_DISCLAIMER}
                    </Col>
                  )}
                  <Col
                    xs={12}
                    md={styleProps.isEscapeHatch || styleProps.isVinByAddressOverlay ? 12 : btnWidthMd}
                    className={classnames({
                      'px-0': styleProps.isLocationStyleStep,
                      'mt-1': isLicensePlateAndVinStep && styleProps.isAddressTab && styleProps.isVinByAddressOverlay,
                    })}
                  >
                    <AppraisalTabsSubmitButton
                      block
                      buttonClassName={classnames(
                        'heading-5 text-white epo-license-plate-continue-btn btn-responsive-md-down',
                        {
                          'py-1': styleProps.isEscapeHatch && !styleProps.isVinByAddressOverlay,
                          'py-0_5': !styleProps.isEscapeHatch && !styleProps.isVinByAddressOverlay,
                        }
                      )}
                      onSubmit={onSubmit}
                      ctaText={ctaText}
                      isSubmitting={isSubmitting || (wasFormSubmitted && styleProps.isVinByAddressOverlay && !hasError)}
                      isDisabled={
                        isSubmitting ||
                        (wasFormSubmitted && (!isLpValid || (styleProps.isVinByAddressOverlay && !hasError)))
                      }
                      data-tracking-id="epo_submit_license_plate"
                      data-test="submit-license-plate-btn"
                      data-tracking-value={ctaText}
                      aria-label={ctaText}
                      isActiveTab={isActiveTab || styleProps.isAddressTab}
                    />
                    {styleProps.isMobile && (
                      <LpFieldErrors
                        uniqInputId={uniqInputId}
                        shouldShowVinError={shouldShowVinError}
                        decodedVinError={decodedVinError}
                        errorMap={errorMap}
                        classes="mt-0_5"
                      />
                    )}
                  </Col>
                  {tabContentModal && (
                    <Col
                      xs={12}
                      md={styleProps.isEscapeHatch ? 12 : 6}
                      className={classnames('mt-1 text-center', {
                        'text-md-start mt-md-0': !styleProps.isEscapeHatch,
                      })}
                    >
                      {tabContentModal}
                    </Col>
                  )}
                </Row>
              )}
            </Fragment>
          )}
        </div>
      </div>
      {shouldRenderPrimaryCta && errorMap[LP_ERROR_KEYS.LICENSE_PLATE_WIDE] && (
        <FieldError
          id={`license-plate-landing-input${uniqInputId}-error`}
          error={errorMap[LP_ERROR_KEYS.LICENSE_PLATE_WIDE]}
          classes={classnames('mt-1', {
            'license-plate-tab-content-verbose-error mt-lg-0': styleProps.isBackgroundStyleTabEnabled,
          })}
        />
      )}
      <MultipleVinsModal
        isOpen={isMultipleVinsModalOpen}
        toggle={closeMultipleVinsModal}
        vins={vinFromLicensePlate?.vins}
        onSelectVin={handleVinFromMultipleVinsSelect}
        vehicle={vehicle}
        isAppraisalValuePage={isAppraisalValuePage}
        isLookUpStyleView={resolvedView.isLookUpStyleView}
        onOpen={onMultipleVinsModalOpen}
        onClose={onMultipleVinsModalClose}
        params={params}
        parentCreativeId={creativeId}
      />
    </FormWrapper>
  );
}

LicensePlateTabUI.propTypes = {
  vinFromLicensePlate: PropTypes.shape({
    vin: PropTypes.string,
    service: PropTypes.string,
    vins: PropTypes.arrayOf(PropTypes.string),
  }),
  licensePlateLookupError: PropTypes.bool,
  vinDecodeResponse: PropTypes.shape({
    results: VehicleVinEntities.VinStylesBasicInfo,
    matchType: PropTypes.string,
    status: PropTypes.number,
    hasError: PropTypes.bool,
  }),
  ctaText: PropTypes.string,
  vehicle: PropTypes.shape({}),
  vinEligibility: PropTypes.shape({}),
  vinTabLink: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  hideVinLicensePlateBtn: PropTypes.bool,
  tabContentModal: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  decodedVinError: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onCompleteCallback: PropTypes.func,
  clearVin: PropTypes.func,
  clearDecodedVinEligibility: PropTypes.func,
  isAppraisalValuePage: PropTypes.bool,
  isLicensePlateAndVinStep: PropTypes.bool,
  isActiveTab: PropTypes.bool,
  isLpAndVinFormComponent: PropTypes.bool,
  isSecondary: PropTypes.bool,
  setStylesAndVin: PropTypes.func,
  storage: PropTypes.shape({
    get: PropTypes.func,
    set: PropTypes.func,
  }),
  apiMetrics: ApiMetrics,
  view: appraisalTabViewPropType,
};

export const stateToPropsConfig = {
  vinDecodeResponse: bindToPath(({ vin }) => buildStylesBasicPathFromVin({ vin }), VehicleVinModel),
  vinFromLicensePlate: bindToPath(
    ({ licensePlate, stateCode }) => buildLicensePlateStateCodeForVinPath({ licensePlate, stateCode }),
    PartnerOfferModel
  ),
};

export const mapStateToProps = state => ({
  licensePlateLookupError: getLicensePlateErrorState(state),
});

const LicensePlateTabConnected = connect(mapStateToProps)(
  connectToModel(withMetrics(LicensePlateTabUI), stateToPropsConfig)
);

// Context-to-props bridge for the connected License Plate tab.
export function LicensePlateTab(props) {
  const { vin, licensePlate, stateCode } = useAppraisalTabsState();

  return <LicensePlateTabConnected {...props} vin={vin} licensePlate={licensePlate} stateCode={stateCode} />;
}
